/**
 * ManualData — self-contained "Receipt Sheets for Dummies" documentation drawer.
 * Rendered as a glassmorphism overlay when the header "Docs & Manual" nav item is clicked.
 * Visible only to Pro-tier and Business-tier user sessions (gated in DashboardLayout).
 */
import { X, BookOpen } from "lucide-react";

const SECTIONS = [
  {
    id: "getting-started",
    title: "1 · Getting Started",
    body: [
      "Welcome to Receipt Sheets — the receipt vault built for audit-proof filing.",
      "After you subscribe (Pro $9.99/mo or Business $19.99/mo), your dashboard unlocks the full ingestion stack: email, SMS, e-commerce sync, and voice logging.",
      "Every receipt is automatically OCR'd by GPT-5.2 Vision, categorized, and grouped into IRS Schedule C line-items ready for your accountant.",
    ],
  },
  {
    id: "ingestion",
    title: "2 · Getting Receipts Into the App",
    body: [
      "SCAN — Tap the Scan tab and either drag a photo, snap one from your phone camera, or upload a PDF. GPT-5.2 Vision extracts merchant, date, line-items, and total in ~3 seconds.",
      "EMAIL — Every account gets a magic forwarding alias (e.g. alex@receiptsheets.com). Forward any receipt email from your inbox to that address and it auto-materializes on your dashboard.",
      "SMS — Text a photo of a paper receipt to +1-555-RSHEETS. The OCR pipeline runs and you get a confirmation reply.",
      "E-COMMERCE SYNC — On the Ingestion tab, click Activate Live Sync for Amazon, Walmart, or DoorDash. Follow the 4-step Gmail/Outlook filter setup — future orders auto-forward and materialize on checkout.",
      "VOICE — Tap the microphone in the AI Assistant and say “I spent forty-five dollars on gas at Chevron this morning.” The dry-run confirmation card lets you tweak before it commits.",
    ],
  },
  {
    id: "categorization",
    title: "3 · Categorization & Deductions",
    body: [
      "Every receipt is auto-tagged into an IRS-recognized category (Meals · Travel · Software · Office Supplies · Transportation · Other).",
      "Deductible line-items automatically flow into your Schedule C export. Meals are pre-halved (50% rule).",
      "Donations get a separate ledger with acknowledgement-letter export for cash and non-cash gifts.",
    ],
  },
  {
    id: "tax-sheet",
    title: "4 · Tax Sheet & Schedule C Export",
    body: [
      "The Receipts tab has a Multi-Year Vault (2024 / 2025 / 2026 chips). Pick a year and click Export Schedule C.",
      "You get a full IRS-formatted PDF binder grouped by line item, with per-receipt deductible amounts and totals.",
      "Check the 'Send to my accountant' checkbox + type your CPA's email. On export, the PDF is emailed via Brevo with a beautiful transactional letter — attachment included.",
    ],
  },
  {
    id: "team",
    title: "5 · Team Directory & Invites (Business Tier)",
    body: [
      "Business-tier accounts unlock the Team Directory tab under Enterprise.",
      "Invite teammates by email — existing users are linked instantly, new emails receive a Brevo magic-link that auto-adopts their seat on first sign-in.",
      "Every card swipe, SMS reconciliation, and expense receipt from any teammate flows into a single shared live-ops stream.",
      "Manager view surfaces a real-time neon-emerald ticker (top-of-screen) for card authorizations across the whole org.",
    ],
  },
  {
    id: "smart-visa",
    title: "6 · Smart Visa (Business Tier)",
    body: [
      "Design corporate cards under Enterprise → Virtual Cards. Toggle 'Issue New Employee Card' to spin one up.",
      "Every mock swipe fires a text-message reconciliation prompt to the employee. They text back a category keyword (‘gas’, ‘meals’, ‘1’, ‘2’, etc.) — the receipt auto-categorizes.",
      "Freeze / unfreeze cards from the Issuing Management Panel. Frozen cards dim, glow red, and reject the swipe simulator.",
    ],
  },
  {
    id: "health-score",
    title: "7 · Financial Health Score",
    body: [
      "Your dashboard top card shows a 0-100 composite score derived from:",
      "  • Deduction Efficiency — % of spending that's tax-deductible.",
      "  • Burn Health — how far you are under your monthly operating budget.",
      "  • CPA Readiness — % of receipts with all four required fields populated.",
      "Tier: Elite ≥80 · Strong ≥60 · Building ≥40 · Needs work.",
      "Improve the composite two weeks in a row and you'll unlock the orange-flame Streak badge — build a multi-week streak for the highest bragging rights.",
    ],
  },
  {
    id: "security",
    title: "8 · Security & Privacy",
    body: [
      "All data is encrypted in transit (TLS 1.2+) and at rest (AES-256).",
      "Receipts are retained for 7 years to meet IRS audit requirements. Delete-on-request honored in 30 days.",
      "Two-Factor Authentication (2FA) toggle is available under Billing → Security.",
      "Public uptime dashboard lives at /status — Statuspage-grade transparency with a 90-day heatmap per service.",
    ],
  },
  {
    id: "shortcuts",
    title: "9 · Keyboard Shortcuts & Power Moves",
    body: [
      "⌘/Ctrl-K — Open the AI Assistant.",
      "⌘/Ctrl-U — Jump straight to Scan / Upload.",
      "Voice on the AI Assistant — Hold the mic icon to log a receipt hands-free.",
      "Tap any dashboard widget to drill into the underlying receipts.",
    ],
  },
  {
    id: "faq",
    title: "10 · FAQ",
    body: [
      "Q: Can I cancel any time? A: Yes — Billing → Manage Subscription → Cancel. Pro-rated refunds honored within 30 days.",
      "Q: Do you support multiple currencies? A: All receipts persist original + USD-normalized totals. Reports use USD.",
      "Q: What if my CPA prefers CSV? A: Every export screen has a CSV button next to the Schedule C PDF button.",
      "Q: Can I switch tiers? A: Anytime — Billing → Manage → Change Plan. Business tier unlocks the org / team / Smart Visa suite.",
    ],
  },
];


export default function ManualData({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      data-testid="manual-overlay"
      className="fixed inset-0 z-[9600] flex items-start justify-center pt-20 pb-6 px-4"
      style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        data-testid="manual-drawer"
        className="glass rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(2,6,23,0.98) 100%)",
          border: "1px solid rgba(16,185,129,0.4)",
          boxShadow: "0 40px 80px -20px rgba(16,185,129,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-emerald-400" />
            <div>
              <div className="text-white font-semibold tracking-tight text-lg" data-testid="manual-title">
                Receipt Sheets for Dummies
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mt-0.5">
                The complete how-to manual · v1.0
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="manual-close"
            className="text-slate-400 hover:text-white p-1 rounded"
            aria-label="Close manual"
          >
            <X size={16} />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-6 py-6 space-y-8"
          data-testid="manual-content"
        >
          <p className="text-slate-400 text-sm leading-relaxed">
            Skim the sections below to master every corner of the platform. Sections are in
            teach-me-anything order — no prior accounting knowledge required.
          </p>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} data-testid={`manual-section-${s.id}`}>
              <h3 className="text-white text-lg font-semibold tracking-tight mb-3">{s.title}</h3>
              <ul className="space-y-2.5">
                {s.body.map((line, i) => (
                  <li key={i} className="text-slate-200 text-[15px] leading-relaxed">
                    {line}
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <div className="pt-6 border-t border-white/5 text-[11px] font-mono uppercase tracking-widest text-slate-500 text-center">
            Receipt Sheets · SOC 2 · 256-bit AES · GDPR · Need more help? support@receiptsheets.com
          </div>
        </div>
      </div>
    </div>
  );
}
